import api from '../api/client';

export interface CostCategory {
  id: number;
  code: string;
  nameSv: string;
  nameEn: string;
  tcoBucket: string;
  includeInImport?: boolean;
}

export interface CostTypeMapping {
  id: number | null;
  organizationId: string | null;
  supplierKey: string;
  sourceCode: string;
  sourceLabel: string | null;
  costCategoryId: number;
  categoryCode: string;
  includeInImport: boolean;
  isDefault: boolean;
}

export interface CostInvoiceImportListItem {
  id: number;
  originalFilename: string;
  invoiceDate: string | null;
  rowCount: number;
  rowsImported: number;
  rowsSkipped: number;
  rowsUnmapped: number;
  rowsUnmatchedVehicle: number;
  importedAt: string;
}

export interface CostInvoiceImportFileResult {
  filename: string;
  duplicate: boolean;
  importId?: number;
  invoiceDate?: string | null;
  rowCount?: number;
  rowsImported?: number;
  rowsSkipped?: number;
  rowsUnmapped?: number;
  rowsUnmatchedVehicle?: number;
  unmappedCodes?: Array<{ sourceCode: string; sourceLabel: string; count: number }>;
  autoMappedCodes?: Array<{
    sourceCode: string;
    sourceLabel: string;
    categoryCode: string;
    count: number;
  }>;
  unmatchedRegistrations?: string[];
  createdVehicles?: string[];
  carInfoFailed?: string[];
  linesLinked?: number;
  parseErrors?: string[];
  message?: string;
}

export interface VehicleCostLine {
  id: number;
  invoiceNumber: string;
  invoiceDate: string | null;
  contractNo: string | null;
  sourceCode: string;
  sourceTypeLabel: string | null;
  categoryCode: string;
  categoryName: string;
  description: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  amountExclVat: number;
  vat: number;
  amountInclVat: number;
  costCenterCode: string | null;
}

const base = '/api/cost-invoices';

export async function listCostCategories(): Promise<CostCategory[]> {
  const res = await api.get<{ success: boolean; data: CostCategory[] }>(`${base}/categories`);
  return res.data.data ?? [];
}

export async function listCostMappings(supplier: string): Promise<CostTypeMapping[]> {
  const res = await api.get<{ success: boolean; data: CostTypeMapping[] }>(`${base}/mappings`, {
    params: { supplier },
  });
  return res.data.data ?? [];
}

export async function saveCostMappings(
  supplier: string,
  mappings: Array<{
    sourceCode: string;
    sourceLabel?: string | null;
    costCategoryId: number;
    includeInImport: boolean;
  }>
): Promise<CostTypeMapping[]> {
  const res = await api.put<{ success: boolean; data: CostTypeMapping[] }>(`${base}/mappings`, {
    supplier,
    mappings,
  });
  return res.data.data ?? [];
}

export async function listCostInvoiceImports(supplier: string): Promise<CostInvoiceImportListItem[]> {
  const res = await api.get<{ success: boolean; data: CostInvoiceImportListItem[] }>(`${base}/imports`, {
    params: { supplier },
  });
  return res.data.data ?? [];
}

export async function uploadCostInvoiceFiles(
  files: File[],
  supplier: string
): Promise<CostInvoiceImportFileResult[]> {
  const results: CostInvoiceImportFileResult[] = [];
  // En fil per request så nginx 1m-default inte stoppar en hel mapp.
  for (const file of files) {
    const form = new FormData();
    form.append('supplier', supplier);
    form.append('files', file);
    const res = await api.post<{ success: boolean; data: CostInvoiceImportFileResult[] }>(
      `${base}/import`,
      form,
      { timeout: 180000 }
    );
    results.push(...(res.data.data ?? []));
  }
  return results;
}

export async function listVehicleCostLines(vehicleId: number): Promise<VehicleCostLine[]> {
  const res = await api.get<{ success: boolean; data: VehicleCostLine[] }>(
    `${base}/vehicles/${vehicleId}/lines`
  );
  return res.data.data ?? [];
}

export interface CostAnalysisCategory {
  categoryCode: string;
  categoryName: string;
  amountExclVat: number;
  lineCount: number;
}

export interface CostAnalysisVehicleRow {
  vehicleId: number;
  registrationNumber: string;
  brand: string | null;
  model: string | null;
  organizationId: string;
  organizationName: string;
  lineCount: number;
  totalExclVat: number;
  totalInclVat: number;
  categories: CostAnalysisCategory[];
}

export interface CostAnalysisGroupResult {
  summary: {
    vehicleCount: number;
    vehiclesWithCost: number;
    lineCount: number;
    unmatchedLineCount: number;
    unmatchedTotalExclVat: number;
    totalExclVat: number;
    totalInclVat: number;
    categories: CostAnalysisCategory[];
  };
  vehicles: CostAnalysisVehicleRow[];
}

export interface CostAnalysisLineRow extends VehicleCostLine {
  vehicleId: number;
  registrationNumber: string;
}

export interface CostAnalysisSelectionResult {
  summary: {
    vehicleCount: number;
    lineCount: number;
    totalExclVat: number;
    totalInclVat: number;
    categories: CostAnalysisCategory[];
  };
  lines: CostAnalysisLineRow[];
}

export interface CostAnalysisQuery {
  organizationId: string;
  startDate?: string;
  endDate?: string;
}

export async function getCostAnalysisGroup(query: CostAnalysisQuery): Promise<CostAnalysisGroupResult> {
  const res = await api.get<{ success: boolean; data: CostAnalysisGroupResult }>(`${base}/analysis/group`, {
    params: {
      organizationId: query.organizationId,
      startDate: query.startDate || undefined,
      endDate: query.endDate || undefined,
    },
  });
  return res.data.data;
}

export async function getCostAnalysisLines(
  query: CostAnalysisQuery & { vehicleIds: number[] }
): Promise<CostAnalysisSelectionResult> {
  const res = await api.get<{ success: boolean; data: CostAnalysisSelectionResult }>(
    `${base}/analysis/lines`,
    {
      params: {
        organizationId: query.organizationId,
        vehicleIds: query.vehicleIds.join(','),
        startDate: query.startDate || undefined,
        endDate: query.endDate || undefined,
      },
    }
  );
  return res.data.data;
}

export interface ContractDocumentImportResult {
  filename: string;
  duplicate: boolean;
  importId?: number;
  vehicleId?: number | null;
  registrationNumber?: string | null;
  attached?: boolean;
  economyUpdated?: boolean;
  aiUsed?: boolean;
  unmatched?: boolean;
  message?: string;
}

export interface ContractDocumentListItem {
  id: number;
  originalFilename: string;
  registrationNumber: string;
  vehicleId: number | null;
  driverName: string | null;
  contractNo: string | null;
  rentStart: string | null;
  rentEnd: string | null;
  costMonthLeasing: number | null;
  aiUsed: boolean;
  importedAt: string;
}

export async function listContractDocuments(): Promise<ContractDocumentListItem[]> {
  const res = await api.get<{ success: boolean; data: ContractDocumentListItem[] }>(
    `${base}/contracts`
  );
  return res.data.data ?? [];
}

export async function uploadContractDocuments(files: File[]): Promise<ContractDocumentImportResult[]> {
  const results: ContractDocumentImportResult[] = [];
  for (const file of files) {
    const form = new FormData();
    form.append('files', file);
    const res = await api.post<{ success: boolean; data: ContractDocumentImportResult[] }>(
      `${base}/contracts/import`,
      form,
      { timeout: 180000 }
    );
    results.push(...(res.data.data ?? []));
  }
  return results;
}

export interface AyvensFuelImportResult {
  filename: string;
  invoiceNumber: string;
  rowCount: number;
  imported: number;
  skippedExisting: number;
  flaggedDuplicates: number;
  createdVehicles: string[];
  parseErrors: string[];
}

export async function uploadAyvensFuelFiles(files: File[]): Promise<AyvensFuelImportResult[]> {
  const results: AyvensFuelImportResult[] = [];
  for (const file of files) {
    const form = new FormData();
    form.append('files', file);
    const res = await api.post<{ success: boolean; data: AyvensFuelImportResult[] }>(
      `${base}/fuel/import`,
      form,
      { timeout: 180000 }
    );
    results.push(...(res.data.data ?? []));
  }
  return results;
}

export function isPdfFile(file: File): boolean {
  return file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
}

export function isSpreadsheetFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}
